import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { getWallets, getTransactions, getReportSummary, addCashIn, addCashOut, getCategories, getCurrentUser } from '../utils/api';
import { toast } from 'sonner';
import { useNumberFormat } from '../hooks/useNumberFormat';
import { motion } from 'framer-motion';
import { Wallet, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Plus, Shield, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { formatCurrency } from '../utils/formatCurrency';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const walletBrandColors = {
  mpesa: '#4CAF50',
  crdb: '#00A859',
  airtel: '#FF0000',
  custom: '#064E3B'
};

export const Dashboard = () => {
  const [wallets, setWallets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isQuickCashInOpen, setIsQuickCashInOpen] = useState(false);
  const [isQuickCashOutOpen, setIsQuickCashOutOpen] = useState(false);
  
  const currentUser = getCurrentUser();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  
  // Use number formatter for amounts
  const cashInAmount = useNumberFormat('');
  const cashOutAmount = useNumberFormat('');
  
  const [cashInData, setCashInData] = useState({
    wallet_id: '',
    source: '',
    narration: ''
  });
  
  const [cashOutData, setCashOutData] = useState({
    wallet_id: '',
    category_id: '',
    phone_number: '',
    narration: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    // Super Admin doesn't have company context, so skip data fetch
    if (isSuperAdmin) {
      setLoading(false);
      return;
    }
    
    try {
      const [walletsData, transactionsData, summaryData, categoriesData] = await Promise.all([
        getWallets(),
        getTransactions(),
        getReportSummary(),
        getCategories()
      ]);
      
      setWallets(walletsData);
      setTransactions(transactionsData.slice(0, 10));
      setSummary(summaryData);
      setCategories(categoriesData);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickCashIn = async (e) => {
    e.preventDefault();
    try {
      await addCashIn(
        cashInData.wallet_id,
        parseFloat(cashInAmount.numericValue),
        cashInData.source,
        cashInData.narration
      );
      toast.success('Cash in recorded!');
      setIsQuickCashInOpen(false);
      setCashInData({ wallet_id: '', source: '', narration: '' });
      cashInAmount.reset();
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to record cash in');
    }
  };

  const handleQuickCashOut = async (e) => {
    e.preventDefault();
    try {
      await addCashOut(
        cashOutData.wallet_id,
        cashOutData.category_id,
        parseFloat(cashOutAmount.numericValue),
        cashOutData.phone_number,
        cashOutData.narration
      );
      toast.success('Cash out recorded!');
      setIsQuickCashOutOpen(false);
      setCashOutData({ wallet_id: '', category_id: '', phone_number: '', narration: '' });
      cashOutAmount.reset();
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to record cash out');
    }
  };

  const totalBalance = wallets.reduce((sum, w) => sum + w.balance, 0);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-emerald-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-stone-600">Loading dashboard...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Super Admin View */}
      {isSuperAdmin && (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 lg:pb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl font-bold text-stone-900 tracking-tight mb-2">Admin Dashboard</h1>
            <p className="text-stone-600">Welcome back, Super Admin! Manage your system from here.</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid gap-4 sm:grid-cols-2"
          >
            <Link
              to="/admin"
              className="bg-gradient-to-br from-purple-900 to-purple-700 hover:from-purple-800 hover:to-purple-600 text-white rounded-2xl p-6 transition-all shadow-lg hover:shadow-xl"
              data-testid="admin-panel-card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90 mb-1">Quick Access</p>
                  <p className="text-2xl font-bold">Admin Panel</p>
                  <p className="text-sm opacity-80 mt-2">Manage companies & users</p>
                </div>
                <Shield className="w-12 h-12 opacity-90" strokeWidth={1.5} />
              </div>
            </Link>

            <Link
              to="/audit-logs"
              className="bg-gradient-to-br from-stone-800 to-stone-600 hover:from-stone-700 hover:to-stone-500 text-white rounded-2xl p-6 transition-all shadow-lg hover:shadow-xl"
              data-testid="audit-logs-card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90 mb-1">System Activity</p>
                  <p className="text-2xl font-bold">Audit Logs</p>
                  <p className="text-sm opacity-80 mt-2">View all system changes</p>
                </div>
                <Building2 className="w-12 h-12 opacity-90" strokeWidth={1.5} />
              </div>
            </Link>
          </motion.div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800">
            <p className="font-medium">Note:</p>
            <p className="text-sm mt-1">As a Super Admin, you manage the system but don't have a company context. To view wallets and transactions, log in as a Manager assigned to a company.</p>
          </div>
        </div>
      )}

      {/* Manager View */}
      {!isSuperAdmin && (
      <div className="max-w-7xl mx-auto space-y-8 pb-20 lg:pb-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl font-bold text-stone-900 tracking-tight mb-2">Dashboard</h1>
          <p className="text-stone-600">Welcome back! Here's your financial overview</p>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="grid grid-cols-2 gap-3 sm:gap-4"
        >
          <button
            onClick={() => setIsQuickCashInOpen(true)}
            data-testid="quick-cash-in-button"
            className="bg-gradient-to-br from-emerald-900 to-emerald-700 hover:from-emerald-800 hover:to-emerald-600 text-white rounded-2xl p-4 sm:p-6 transition-all shadow-lg hover:shadow-xl active:scale-95"
          >
            <div className="flex items-center justify-between">
              <div className="text-left">
                <p className="text-xs sm:text-sm opacity-90 mb-1">Quick Action</p>
                <p className="text-lg sm:text-2xl font-bold">Cash In</p>
              </div>
              <ArrowUpRight className="w-8 h-8 sm:w-10 sm:h-10 opacity-90" strokeWidth={2} />
            </div>
          </button>

          <button
            onClick={() => setIsQuickCashOutOpen(true)}
            data-testid="quick-cash-out-button"
            className="bg-gradient-to-br from-orange-800 to-orange-600 hover:from-orange-700 hover:to-orange-500 text-white rounded-2xl p-4 sm:p-6 transition-all shadow-lg hover:shadow-xl active:scale-95"
          >
            <div className="flex items-center justify-between">
              <div className="text-left">
                <p className="text-xs sm:text-sm opacity-90 mb-1">Quick Action</p>
                <p className="text-lg sm:text-2xl font-bold">Cash Out</p>
              </div>
              <ArrowDownRight className="w-8 h-8 sm:w-10 sm:h-10 opacity-90" strokeWidth={2} />
            </div>
          </button>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white border border-stone-200 rounded-xl p-4 sm:p-6"
            data-testid="total-balance-card"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm font-medium text-stone-600">Total Balance</p>
              <Wallet className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-900" strokeWidth={1.5} />
            </div>
            <p className="text-xl sm:text-3xl font-bold text-stone-900 break-words">{formatCurrency(totalBalance)}</p>
            <p className="text-xs text-stone-500 mt-1">Across {wallets.length} wallets</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white border border-stone-200 rounded-xl p-4 sm:p-6"
            data-testid="cash-in-card"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm font-medium text-stone-600">Cash In</p>
              <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-700" strokeWidth={1.5} />
            </div>
            <p className="text-xl sm:text-3xl font-bold text-emerald-900 break-words">{formatCurrency(summary?.total_cash_in || 0)}</p>
            <p className="text-xs text-emerald-700 mt-1">Total received</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white border border-stone-200 rounded-xl p-4 sm:p-6"
            data-testid="cash-out-card"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm font-medium text-stone-600">Cash Out</p>
              <ArrowDownRight className="w-4 h-4 sm:w-5 sm:h-5 text-orange-800" strokeWidth={1.5} />
            </div>
            <p className="text-xl sm:text-3xl font-bold text-orange-800 break-words">{formatCurrency(summary?.total_cash_out || 0)}</p>
            <p className="text-xs text-orange-700 mt-1">Total spent</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white border border-stone-200 rounded-xl p-4 sm:p-6"
            data-testid="net-cashflow-card"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm font-medium text-stone-600">Net Cashflow</p>
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-stone-600" strokeWidth={1.5} />
            </div>
            <p className={`text-xl sm:text-3xl font-bold break-words ${
              (summary?.net_cashflow || 0) >= 0 ? 'text-emerald-900' : 'text-orange-800'
            }`}>
              {formatCurrency(summary?.net_cashflow || 0)}
            </p>
            <p className="text-xs text-stone-500 mt-1">Including charges</p>
          </motion.div>
        </div>

        {/* Wallets */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold text-stone-900">Your Wallets</h2>
            <Link to="/wallets">
              <Button className="bg-emerald-900 hover:bg-emerald-800 text-white rounded-full" size="sm" data-testid="view-all-wallets-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Wallet
              </Button>
            </Link>
          </div>
          {wallets.length === 0 ? (
            <div className="bg-white border border-stone-200 rounded-xl p-8 text-center">
              <Wallet className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-600 mb-4">No wallets yet. Create your first wallet to get started!</p>
              <Link to="/wallets">
                <Button className="bg-emerald-900 hover:bg-emerald-800 text-white rounded-full" data-testid="create-first-wallet-button">
                  Create Wallet
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {wallets.map((wallet, index) => (
                <motion.div
                  key={wallet.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  className="bg-white border border-stone-200 rounded-xl p-6 hover:border-emerald-500/50 hover:shadow-lg transition-all cursor-pointer"
                  data-testid={`wallet-card-${wallet.id}`}
                  style={{ borderTopColor: walletBrandColors[wallet.type] }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-sm font-medium text-stone-600 uppercase tracking-wide">{wallet.type}</p>
                      <p className="text-xl font-bold text-stone-900 mt-1">{wallet.name}</p>
                    </div>
                    <Wallet className="w-6 h-6" style={{ color: walletBrandColors[wallet.type] }} strokeWidth={1.5} />
                  </div>
                  <div className="pt-4 border-t border-stone-100">
                    <p className="text-sm text-stone-600 mb-1">Balance</p>
                    <p className="text-2xl font-bold text-stone-900">{formatCurrency(wallet.balance)}</p>
                    <p className="text-xs text-stone-500 mt-2">Charge: {wallet.transaction_charge_rate}%</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Recent Transactions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold text-stone-900">Recent Transactions</h2>
            <Link to="/transactions">
              <Button variant="ghost" className="text-emerald-900 hover:text-emerald-800" data-testid="view-all-transactions-button">
                View all
              </Button>
            </Link>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
            {transactions.length === 0 ? (
              <div className="p-8 text-center">
                <TrendingUp className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-600">No transactions yet</p>
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {transactions.map((transaction, index) => (
                  <div key={transaction.id} className="p-4 hover:bg-stone-50 transition-colors" data-testid={`transaction-item-${index}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          transaction.type === 'cash_in' ? 'bg-emerald-50' : 'bg-orange-50'
                        }`}>
                          {transaction.type === 'cash_in' ? (
                            <ArrowUpRight className="w-5 h-5 text-emerald-700" strokeWidth={1.5} />
                          ) : (
                            <ArrowDownRight className="w-5 h-5 text-orange-700" strokeWidth={1.5} />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-stone-900">
                            {transaction.type === 'cash_in' ? transaction.source : transaction.category_name}
                          </p>
                          <p className="text-sm text-stone-500">{transaction.wallet_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${
                          transaction.type === 'cash_in' ? 'text-emerald-900' : 'text-orange-800'
                        }`}>
                          {transaction.type === 'cash_in' ? '+' : '-'}{formatCurrency(transaction.amount)}
                        </p>
                        <p className="text-xs text-stone-500">
                          {new Date(transaction.date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>

      {/* Quick Cash In Dialog */}
      <Dialog open={isQuickCashInOpen} onOpenChange={setIsQuickCashInOpen}>
        <DialogContent data-testid="quick-cash-in-dialog" className="max-w-[90vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Quick Cash In</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleQuickCashIn} className="space-y-3 sm:space-y-4">
            <div>
              <Label className="text-sm">Amount (TZS)</Label>
              <Input
                type="tel"
                data-testid="quick-cash-in-amount"
                value={cashInAmount.displayValue}
                onChange={cashInAmount.handleChange}
                placeholder="0"
                className="bg-stone-50 text-2xl sm:text-3xl h-16 sm:h-20 font-bold text-center"
                autoFocus
                required
              />
              <p className="text-xs text-stone-500 mt-1 text-center">
                {cashInAmount.numericValue ? `TZS ${cashInAmount.displayValue}` : 'Enter amount'}
              </p>
            </div>
            <div>
              <Label className="text-sm">Wallet</Label>
              <Select value={cashInData.wallet_id} onValueChange={(value) => setCashInData({ ...cashInData, wallet_id: value })}>
                <SelectTrigger className="bg-stone-50 h-11" data-testid="quick-cash-in-wallet">
                  <SelectValue placeholder="Select wallet" />
                </SelectTrigger>
                <SelectContent>
                  {wallets.map(w => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Source</Label>
              <Input
                data-testid="quick-cash-in-source"
                value={cashInData.source}
                onChange={(e) => setCashInData({ ...cashInData, source: e.target.value })}
                placeholder="e.g. Salary"
                className="bg-stone-50 h-11"
                required
              />
            </div>
            <div>
              <Label className="text-sm">Note (Optional)</Label>
              <Input
                data-testid="quick-cash-in-narration"
                value={cashInData.narration}
                onChange={(e) => setCashInData({ ...cashInData, narration: e.target.value })}
                placeholder="Notes"
                className="bg-stone-50 h-11"
              />
            </div>
            <Button type="submit" className="w-full bg-emerald-900 hover:bg-emerald-800 text-white rounded-full h-12 text-base font-semibold" data-testid="quick-cash-in-submit">
              Record Cash In
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick Cash Out Dialog */}
      <Dialog open={isQuickCashOutOpen} onOpenChange={setIsQuickCashOutOpen}>
        <DialogContent data-testid="quick-cash-out-dialog" className="max-w-[90vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Quick Cash Out</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleQuickCashOut} className="space-y-3 sm:space-y-4">
            <div>
              <Label className="text-sm">Amount (TZS)</Label>
              <Input
                type="tel"
                data-testid="quick-cash-out-amount"
                value={cashOutAmount.displayValue}
                onChange={cashOutAmount.handleChange}
                placeholder="0"
                className="bg-stone-50 text-2xl sm:text-3xl h-16 sm:h-20 font-bold text-center"
                autoFocus
                required
              />
              <p className="text-xs text-stone-500 mt-1 text-center">
                {cashOutAmount.numericValue ? `TZS ${cashOutAmount.displayValue}` : 'Enter amount'}
              </p>
            </div>
            <div>
              <Label className="text-sm">Wallet</Label>
              <Select value={cashOutData.wallet_id} onValueChange={(value) => setCashOutData({ ...cashOutData, wallet_id: value })}>
                <SelectTrigger className="bg-stone-50 h-11" data-testid="quick-cash-out-wallet">
                  <SelectValue placeholder="Select wallet" />
                </SelectTrigger>
                <SelectContent>
                  {wallets.map(w => (
                    <SelectItem key={w.id} value={w.id} className="text-sm">
                      <span className="block truncate">{w.name}</span>
                      <span className="text-xs text-stone-500 block">{formatCurrency(w.balance)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Category</Label>
              <Select value={cashOutData.category_id} onValueChange={(value) => setCashOutData({ ...cashOutData, category_id: value })}>
                <SelectTrigger className="bg-stone-50 h-11" data-testid="quick-cash-out-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Phone (Optional)</Label>
              <Input
                type="tel"
                data-testid="quick-cash-out-phone"
                value={cashOutData.phone_number}
                onChange={(e) => setCashOutData({ ...cashOutData, phone_number: e.target.value })}
                placeholder="+255..."
                className="bg-stone-50 h-11"
              />
            </div>
            <div>
              <Label className="text-sm">Note (Optional)</Label>
              <Input
                data-testid="quick-cash-out-narration"
                value={cashOutData.narration}
                onChange={(e) => setCashOutData({ ...cashOutData, narration: e.target.value })}
                placeholder="Notes"
                className="bg-stone-50 h-11"
              />
            </div>
            <Button type="submit" className="w-full bg-orange-800 hover:bg-orange-700 text-white rounded-full h-12 text-base font-semibold" data-testid="quick-cash-out-submit">
              Record Cash Out
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      </div>
      )}
    </DashboardLayout>
  );
};